import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, X, Mail, Loader2, Crown } from "lucide-react";

/**
 * Team invites panel shown inside project detail.
 * Only the project owner sees the invite form + remove buttons.
 */
export default function TeamPanel({ project, onUpdate }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const isOwner = project.is_owner;
  const members = project.members || [];

  const invite = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/invite`, { email: email.trim() });
      if (data.already_invited) toast.info(`${data.email} was already invited`);
      else toast.success(`Invite sent to ${data.email}`);
      setEmail("");
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (memberEmail) => {
    if (!window.confirm(`Remove ${memberEmail} from this project?`)) return;
    try {
      await api.delete(`/projects/${project.id}/members/${encodeURIComponent(memberEmail)}`);
      toast.success("Removed");
      onUpdate?.();
    } catch { toast.error("Remove failed"); }
  };

  return (
    <div className="border border-slate-200 bg-white p-6" data-testid="team-panel">
      <div className="mb-4 flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-[#2563EB]" />
        <div className="overline text-[#2563EB]">TEAM</div>
        {!isOwner && <span className="overline text-slate-400">· INVITED VIEW</span>}
      </div>

      <div className="mb-5 space-y-2">
        {/* Owner row */}
        <div className="flex items-center justify-between border border-slate-200 bg-[#FAFAF7] px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Crown className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-slate-800">Owner</span>
            {isOwner && <span className="overline text-slate-400">· YOU</span>}
          </div>
          <span className="overline text-slate-500">OWNER</span>
        </div>

        {members.length === 0 && (
          <div className="border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-400">
            No teammates yet.
          </div>
        )}
        {members.map((m) => (
          <div key={m} className="flex items-center justify-between border border-slate-200 bg-[#FAFAF7] px-3 py-2 text-sm">
            <div className="flex min-w-0 items-center gap-2">
              <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate text-slate-800">{m}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="overline text-slate-400">MEMBER</span>
              {isOwner && (
                <button
                  onClick={() => remove(m)}
                  data-testid="team-remove-member"
                  className="border border-slate-200 p-1 text-slate-500 hover:border-red-500/60 hover:text-red-400"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {isOwner && (
        <form onSubmit={invite} className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px] flex-1">
            <label className="overline mb-2 block text-slate-500">Invite teammate by email</label>
            <div className="flex items-center border border-slate-200 bg-[#FAFAF7] pl-3">
              <Mail className="h-4 w-4 text-slate-400" />
              <input
                data-testid="team-invite-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ra@yourcompany.com"
                className="flex-1 border-none bg-transparent px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-300 focus:outline-none"
              />
            </div>
          </div>
          <button
            data-testid="team-invite-btn"
            disabled={busy || !email.trim()}
            type="submit"
            className="inline-flex items-center gap-2 bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Invite
          </button>
        </form>
      )}
      <p className="overline mt-3 leading-relaxed text-slate-400">
        Invited teammates get read/write access as soon as they sign in with that email.
      </p>
    </div>
  );
}
